import { PlatformSDKShape } from '../PlatformSDKShape';
import { BaseSDKShape } from '../BaseSDKShape';
import { ApplicationContext } from '@wix/editor-platform-contexts';

interface IFontFamily {
  family: string;
  weight: string;
}

interface IFontWeight {
  weight: string;
}

type IFontFamilyAndWeight = IFontFamily & IFontWeight;

type ISelectLinkSharedOptions = {
  origin?: string;
};
export class ElementSDKShape extends BaseSDKShape {
  constructor(
    protected overriddenApplicationContext: Promise<ApplicationContext> | null = null,
    private childPath: number[] = [],
  ) {
    super(overriddenApplicationContext);
  }

  async getStyleDefinitions() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getStyleDefinitions(
      manifestContextId,
      this.childPath,
    );
  }

  async getStyles(): Promise<{
    cssProperties: Record<string, string>;
    cssCustomProperties: Record<string, string>;
  }> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getStyles(
      manifestContextId,
      this.childPath,
    );
  }

  async setStyles({
    cssProperties,
    cssCustomProperties,
  }: {
    cssProperties?: Record<string, string>;
    cssCustomProperties?: Record<string, string>;
  } = {}): Promise<void> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.setStyles(
      manifestContextId,
      this.childPath,
      {
        cssProperties,
        cssCustomProperties,
      },
    );
  }

  async removeStyles(
    values: {
      cssPropertiesKeys?: string[];
      cssCustomPropertiesKeys?: string[];
    } = {},
  ) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.removeStyles(
      manifestContextId,
      this.childPath,
      values,
    );
  }

  async getDataDefinitions() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getDataDefinitions(
      manifestContextId,
      this.childPath,
    );
  }

  async getData() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getData(
      manifestContextId,
      this.childPath,
    );
  }

  async getResolvedData({ dataItemKey }: { dataItemKey: string }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getResolvedData(
      manifestContextId,
      this.childPath,
      {
        dataItemKey,
      },
    );
  }

  async setData(values: {}) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.setData(
      manifestContextId,
      this.childPath,
      values,
    );
  }

  async getPresetDefinitions() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getPresetDefinitions(
      manifestContextId,
      this.childPath,
    );
  }

  async getAppliedPreset() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getAppliedPreset(
      manifestContextId,
      this.childPath,
    );
  }

  async applyPreset(options: { key: string }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.applyPreset(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  // TODO: group type should come from builder enum
  async getDisplayGroupDefinitions(options?: {
    filter?: { groupType?: string };
  }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.getDisplayGroupDefinitions(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  async getState() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getState(
      manifestContextId,
      this.childPath,
    );
  }

  async setState(stateKey: string | null) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.setState(
      manifestContextId,
      this.childPath,
      stateKey,
    );
  }

  async getStateDefinitions() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getStateDefinitions(
      manifestContextId,
      this.childPath,
    );
  }

  /**
   * TODO: use type from the ManifestChangeDetection,
   * but type should be moved to editor-platform-public-interfaces
   */
  async onChange(
    cb: (patch: {
      type: 'cssProperties' | 'cssCustomProperties' | 'data' | 'manifest';
      value: unknown;
    }) => void,
  ): Promise<() => {}> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.onChange(
      manifestContextId,
      this.childPath,
      cb,
    );
  }

  async selectFont(
    params:
      | {
          styleItemKey: string;
        }
      // TODO: get type from common place https://wix.atlassian.net/browse/EP-6480
      | {
          selectedFontFamily?: IFontFamilyAndWeight;
          target?: HTMLElement;
          onChange?: (value: IFontFamilyAndWeight) => void;
          onPreview?: (value: IFontFamilyAndWeight) => void;
        },
  ) {
    try {
      const privateAPI = await this.getPlatformPrivateAPI();
      const manifestContextId = await this.getRequiredManifestContextId();
      const selectedFont = await privateAPI.builderComponent.selectFont(
        manifestContextId,
        this.childPath,
        {
          ...params,
          showWeights: true,
        },
      );
      return selectedFont;
    } catch (error) {
      // TODO: handle error https://wix.atlassian.net/browse/EP-6428
    }
  }

  async selectFontFamily(
    params:
      | {
          styleItemKey: string;
        }
      // TODO: get type from common place https://wix.atlassian.net/browse/EP-6480
      | {
          selectedFontFamily?: IFontFamily;
          target?: HTMLElement;
          onChange?: (value: IFontFamily) => void;
          onPreview?: (value: IFontFamily) => void;
        },
  ) {
    try {
      const privateAPI = await this.getPlatformPrivateAPI();
      const manifestContextId = await this.getRequiredManifestContextId();
      const selectedFont = await privateAPI.builderComponent.selectFont(
        manifestContextId,
        this.childPath,
        {
          ...params,
          showWeights: false,
        },
      );
      return selectedFont;
    } catch (error) {
      // TODO: handle error https://wix.atlassian.net/browse/EP-6428
    }
  }

  async selectMedia(
    params: // TODO: get type from common place  https://wix.atlassian.net/browse/EP-6462
    | {
          dataItemKey: string;
        }
      | {
          isMultiSelect?: boolean;
          category?: string;
        },
  ) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.selectMedia(
      manifestContextId,
      this.childPath,
      params,
    );
  }

  async selectLink(
    params:
      | {
          dataItemKey: string;
          options?: ISelectLinkSharedOptions;
        }
      | {
          value?: object;
          options?: ISelectLinkSharedOptions & {
            linkTypes?: string[];
          };
        },
  ) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.selectLink(
      manifestContextId,
      this.childPath,
      params,
    );
  }

  async selectColor(
    params:
      | { styleItemKey: string }
      // TODO: get the ColorPickerOptions from SDK types https://wix.atlassian.net/browse/EP-6433
      | {
          initialValue?: string;
          mode?: ('solid' | 'gradient')[];
          showBrand?: boolean;
          showCustom?: boolean;
          showAdvancedSettings?: boolean;
          showOpacity?: boolean;
          position?: { x: number; y: number };
          biData?: { origin: string };
          // TODO: Fix when public Color type is available https://wix.atlassian.net/browse/EP-6427
          onChange?: (color: string) => void;
          // TODO: Fix when public Color type is available https://wix.atlassian.net/browse/EP-6427
          onApply?: (color: string) => void;
          onClose?: () => void;
          onCancel?: () => void;
        },
  ) {
    const privateAPI = await this.getPlatformPrivateAPI();
    try {
      const manifestContextId = await this.getRequiredManifestContextId();
      const selectedColor = await privateAPI.builderComponent.selectColor(
        manifestContextId,
        this.childPath,
        params,
      );
      return selectedColor;
    } catch (error) {
      // TODO: handle error https://wix.atlassian.net/browse/EP-6428
    }
  }

  async selectFontWeight(
    params:
      | {
          fontFamilyStyleItemKey: string;
          fontWeightStyleItemKey: string;
        }
      | {
          fontStyleItemKey: string;
        }
      | {
          fontFamily?: IFontFamily;
          target?: HTMLElement;
          onChange?: (value: IFontWeight) => void;
        },
  ): Promise<IFontWeight> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return {
      weight: await privateAPI.builderComponent.selectFontWeight(
        manifestContextId,
        this.childPath,
        params,
      ),
    };
  }

  async selectTextTheme(params: {
    target?: HTMLElement;
    initialValue?: {
      font?: string;
      color?: string;
    };
  }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.builderComponent.selectTextTheme(
      manifestContextId,
      params,
    );
  }

  async getArrayItemsSelectedIndex(options?: {
    arrayItemsDisplayGroupKey?: string;
  }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.getArrayItemsSelectedIndex(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  async setArrayItemsSelectedIndex(options: {
    index: number;
    arrayItemsDisplayGroupKey?: string;
  }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.setArrayItemsSelectedIndex(
      manifestContextId,
      this.childPath,
      options,
    );
  }

  async resetArrayItemsSelectedIndex(options?: {
    arrayItemsDisplayGroupKey?: string;
  }) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();

    return privateAPI.builderComponent.resetArrayItemsSelectedIndex(
      manifestContextId,
      this.childPath,
      options,
    );
  }
}

export default new PlatformSDKShape(ElementSDKShape)
  .withPublicMethod('getDataDefinitions')
  .withPublicMethod('getData')
  .withPublicMethod('getResolvedData')
  .withPublicMethod('setData')
  .withPublicMethod('getStyleDefinitions')
  .withPublicMethod('getStyles')
  .withPublicMethod('setStyles')
  .withPublicMethod('removeStyles')
  .withPublicMethod('getPresetDefinitions')
  .withPublicMethod('getAppliedPreset')
  .withPublicMethod('applyPreset')
  .withPublicMethod('getDisplayGroupDefinitions')
  .withPublicMethod('getState')
  .withPublicMethod('setState')
  .withPublicMethod('onChange')
  .withPublicMethod('getStateDefinitions')
  .withPublicMethod('selectFont')
  .withPublicMethod('selectFontFamily')
  .withPublicMethod('selectMedia')
  .withPublicMethod('selectLink')
  .withPublicMethod('selectColor')
  .withPublicMethod('selectFontWeight')
  .withPublicMethod('selectTextTheme')
  .withPublicMethod('getArrayItemsSelectedIndex')
  .withPublicMethod('setArrayItemsSelectedIndex')
  .withPublicMethod('resetArrayItemsSelectedIndex')
  .build();
